/**
 * The arithmetic behind a card's gestures, and the rule for when one belongs to
 * something else (SPEC-025 D-6, D-9, D-17).
 *
 * Pure, and split from `useCardGestures` for the reason `zoomFrame.js` is split
 * from `usePinchZoom`: none of this needs React or a pointer, all of it is what
 * the hook's jsdom suite actually tests, and the two together were over the
 * repo's file-length limit.
 */

/** How far a gesture travels before its axis is decided, in CSS px. */
export const AXIS_LOCK_PX = 10;

/**
 * The strip down the left edge iOS reads as its own back gesture. A swipe that
 * starts here is conceded rather than raced: a half-swipe that does neither
 * thing is worse than the browser simply winning.
 */
export const EDGE_GUARD_PX = 24;

const THRESHOLD_RATIO = 0.25;
const MIN_THRESHOLD_PX = 56;
const MAX_THRESHOLD_PX = 120;

/**
 * The lift is a shorter gesture than the swipe, measured against a taller box.
 *
 * A quarter of a card's height is 150px on a 13 mini — a haul, and one the thumb
 * makes less comfortably than the same distance across. A card that has to be
 * dragged a third of the way up the screen before it opens reads as stuck, which
 * is the opposite of what a handle-and-lift is for.
 */
const LIFT_RATIO = 0.12;
const MIN_LIFT_PX = 48;
const MAX_LIFT_PX = 96;

const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, label, [role="button"]';

/**
 * A FLICK: short, but fast enough that nobody meant it to stop.
 *
 * The threshold above is a distance, which is predictable and is what makes the
 * card follow the finger honestly — but a reader browsing quickly flicks rather
 * than drags, and a flick that travels 40px in 60ms is unmistakably a swipe. On
 * distance alone it sprang back, and a deck that ignores a confident gesture
 * feels broken rather than firm.
 *
 * Both conditions, not either: far enough to have a direction anybody could
 * name, and fast enough that it was thrown rather than placed.
 */
const FLICK_MIN_PX = 28;
const FLICK_MIN_SPEED = 0.45; // px per ms

export function isFlick({ distance, elapsedMs }) {
    return Math.abs(distance) >= FLICK_MIN_PX
        && elapsedMs > 0
        && Math.abs(distance) / elapsedMs >= FLICK_MIN_SPEED;
}

/**
 * How far this page's swipe has to travel to fire.
 *
 * A page-wide gesture wants a page-relative distance — a quarter of the width
 * reads the same on any device — but only between bounds: a fixed fraction is
 * trivially crossed on a narrow phone and a haul on a tablet.
 */
export function swipeThreshold(width) {
    if (!(width > 0)) {
        return MIN_THRESHOLD_PX;
    }

    return Math.min(Math.max(width * THRESHOLD_RATIO, MIN_THRESHOLD_PX), MAX_THRESHOLD_PX);
}

/** How far a lift has to travel to open the card, against the card's own height. */
export function liftThreshold(height) {
    if (!(height > 0)) {
        return MIN_LIFT_PX;
    }

    return Math.min(Math.max(height * LIFT_RATIO, MIN_LIFT_PX), MAX_LIFT_PX);
}

/**
 * Read a gesture from how far it has travelled.
 *
 * Pure, and separately exported, because it is the whole of the decision and
 * jsdom has no pointer to prove it with.
 *
 * Both axes come back in the SAME shape — axis, direction, progress, isReady —
 * so the caller's two policies are two readings of one object rather than two
 * kinds of thing. What a vertical reading is FOR is decided in the hook, which
 * is where the caller's policy lives.
 *
 * @returns {null|{axis, direction, progress, isReady}} `null` while the gesture
 *   is too small to have an axis yet.
 */
export function readGesture({ dx, dy, threshold, lift = threshold }) {
    if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) {
        return null;
    }

    // Equal parts down and across is vertical: the page keeps the ambiguous
    // ones, because losing a swipe costs a second try and stealing a scroll
    // costs the reader their place.
    if (Math.abs(dy) >= Math.abs(dx)) {
        const progress = Math.min(Math.abs(dy) / lift, 1);

        return {
            axis: 'vertical',
            // The card follows the finger here too: dragging up lifts it open.
            direction: dy < 0 ? 'up' : 'down',
            progress,
            isReady: progress >= 1,
        };
    }

    const progress = Math.min(Math.abs(dx) / threshold, 1);

    return {
        axis: 'horizontal',
        // Content follows the finger: dragging left brings the next plant in
        // from the right, which is the direction a page turns forward.
        direction: dx < 0 ? 'forward' : 'back',
        progress,
        isReady: progress >= 1,
    };
}

/**
 * Does something between `target` and `root` already own sideways?
 *
 * Three things count:
 *
 * - A CONTROL, because a drag that starts on a button is not a page gesture.
 * - A HORIZONTALLY SCROLLABLE BOX, because the companion rail means something by
 *   a sideways drag already. Computed — overflowing content AND an overflow
 *   style that scrolls — rather than told which classes to look for, so a rail
 *   added later inherits the rule instead of rediscovering the bug.
 * - An element that SAYS SO, with `data-sideways="own"`. The picture band claims
 *   sideways for cycling a plant's pictures (SPEC-025 D-8 as revised) and it is
 *   not a scroller: it mounts one picture and swaps it. Without a way to
 *   declare the claim, the only way to make this rule see it would be to give
 *   the band an overflow it does not want, which is a lie told to a stylesheet
 *   so that a hook will believe it.
 */
export function isSidewaysClaimed(target, root, { getComputedStyle } = {}) {
    const readStyle = getComputedStyle
        || (typeof window !== 'undefined' ? window.getComputedStyle.bind(window) : null);

    if (!(target instanceof Element) || !readStyle) {
        return false;
    }

    const control = target.closest(INTERACTIVE_SELECTOR);

    // A control claims sideways — a drag that starts on a button is not a page
    // gesture — UNLESS it has opted into being part of a gesture surface. The
    // card's grab handle is a button, because expanding has to be reachable
    // without a pointer (NFR-04), and it is also the one place the card may be
    // dragged from. Both at once is only expressible if the control can say so.
    if (control && !control.closest('[data-gesture="grab"]')) {
        return true;
    }

    for (let element = target; element && element !== root?.parentElement; element = element.parentElement) {
        // An element never claims sideways FROM ITSELF. The band declares the
        // claim and also mounts the hook that honours it, so without this the
        // picture's own gesture would be turned away by the picture's own
        // notice — and the claim is aimed at the card above it, not at itself.
        if (element !== root && element.dataset?.sideways === 'own') {
            return true;
        }

        const { overflowX } = readStyle(element);

        if ((overflowX === 'auto' || overflowX === 'scroll') && element.scrollWidth > element.clientWidth) {
            return true;
        }
    }

    return false;
}

/**
 * Is this press one this page should read as a gesture at all?
 *
 * A finger arrives here as a `touch` pointer with `button` 0 — the same shape a
 * left mouse press has — which is what makes one code path honest rather than
 * merely tidy. What is turned away is a second finger (`isPrimary` false, so a
 * pinch on the artwork is never half-read as a swipe), a right or middle click,
 * and anything starting in the strip iOS keeps for its own back gesture.
 */
export function startsGesture({ isPrimary, button, clientX }) {
    return Boolean(isPrimary) && button === 0 && clientX > EDGE_GUARD_PX;
}
